import { unwrapResult } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RouteConfig, useNavigate } from '.';
import {
  fromAccounts,
  fromAccountUsers,
  fromAuth,
  fromSubscription,
  useAppDispatch,
} from '../store';
import { useQuery } from './useQuery';
import { usePermission } from '../permission';
import { hasPermission } from '../utils/PermissionCheck';
import { ACCOUNT_SELECTION_ROUTE_QUERY_PARAM } from '../utils/Constants';
import routes, {
  accountSelection,
  order,
  orderConfirmation,
  payment,
  paymentStatus,
  plan,
  starter,
  subscription,
  subscriptionManagement,
} from '../layout/routes';
import { Feature, RouteVersion, SubscriptionSource } from '../models';
import { useLaunchDarkly } from '../launchdarkly';

// Routes accessible only during new account checkout flow (no paid subscriptions)
const NEW_ACCOUNT_ALLOWED_PATHS = [
  plan.path,
  orderConfirmation.path,
  paymentStatus.path,
  starter.path,
  payment.path,
  order.path,
];

export const useAccountAuthenticate = (
  {
    isRootOrAdminAccessible,
    accessibleRoles,
    resource,
    actions,
  }: RouteConfig,
) => {
  const user = useSelector(fromAuth.selectUser);
  const { get, queryDictionary } = useQuery();
  const { permissionMapping } = usePermission();
  const { flags, changeAccount } = useLaunchDarkly();
  const accountId = get('accountId');
  const spEnabled = get('sp') === 'true';
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isFticUpgradeEntry = get('ftic') === 'true' && location.pathname === subscription.path;
  const { navigate, replaceQuery } = useNavigate();
  const isRootOrAdmin = useSelector(fromAuth.selectIsRootOrAdmin);
  const [isAccountAuthenticated, setIsAccountAuthenticated] = useState(false);
  const [redirectToForbidden, setRedirectToForbidden] = useState(false);
  const [redirectToNotFound, setRedirectToNotFound] = useState(false);
  const [redirectToUpgradeBlockedForbidden, setRedirectToUpgradeBlockedForbidden] = useState(false);
  const [accountAuthenticationError, setAccountAuthenticationError] = useState(false);
  const [verificationAttempt, setVerificationAttempt] = useState(0);

  useEffect(() => {
    // a superseded run (accountId changed mid-flight) must not touch anything on
    // resume: doSetSelectedAccount mutates the X-Account-Id axios header, navigate
    // rewrites the URL, changeAccount repoints the LaunchDarkly context
    let cancelled = false;

    const verifyStarterPackage = async (accountId: number) => {
      const getUrlPath = (
        currentRoute: RouteConfig | undefined,
        isOptedInStarterPackage: boolean,
      ): string => {
        // in case of account is opted in starter package
        if (isOptedInStarterPackage) {
          // if current route is new v2 route, navigate to replace path if available
          // else navigate to subscription management page
          if (currentRoute?.version === RouteVersion.V2) {
            return currentRoute.replacePath ?? subscriptionManagement.path;
          }
          return currentRoute?.path ?? subscriptionManagement.path;
        }

        // in case of account is not opted in starter package
        // and current route is v1 route, navigate to replace path if available
        // else navigate to new subscription page
        if (currentRoute?.version === RouteVersion.V1) {
          return currentRoute.replacePath ?? subscription.path;
        }

        return currentRoute?.path ?? subscription.path;
      };

      await dispatch(fromAccounts.doCheckStarterPackage({ accountId, spEnabled }))
        .then(unwrapResult)
        .then(isOptedInStarterPackage => {
          if (cancelled) return;
          const currentRoute = routes.find(route => location.pathname === route.path);
          navigate(
            getUrlPath(currentRoute, isOptedInStarterPackage),
            replaceQuery(queryDictionary()),
          );
        });
    };

    const verifyUserAccountRoleAndPermission = async (
      userId: number,
      accountId: number,
    ): Promise<boolean> => {
      const currentAccountUser = await dispatch(fromAccountUsers.doGetByUserIdAndAccountId({
        userId,
        accountId,
      })).then(unwrapResult);

      const accountUser = currentAccountUser.total > 0
        ? currentAccountUser.data.at(0) : undefined;

      if (cancelled) return false;

      if ((isRootOrAdminAccessible && isRootOrAdmin)
        || (accountUser
          && (accessibleRoles === undefined || accessibleRoles?.includes(accountUser.role)))
        || hasPermission(permissionMapping, resource, actions)
      ) {
        dispatch(fromAccounts.doSetSelectedAccount(accountUser?.account));
        return true;
      }

      if (isFticUpgradeEntry) {
        try {
          const result = await dispatch(fromSubscription.doFetchSubscriptions({ accountId }))
            .then(unwrapResult);
          const hasTrueSubscription = result.subscriptionResponse.some(
            sub => sub.feature === Feature.TRUE_TEAM
              || sub.feature === Feature.TRUE_ENTERPRISE
              || sub.feature === Feature.TRUE_PLATFORM_ONLY
              || sub.feature === Feature.TRUE_AUTOMATION,
          );

          if (!hasTrueSubscription) {
            if (!cancelled) setRedirectToUpgradeBlockedForbidden(true);
            return false;
          }
        } catch {
          if (!cancelled) setRedirectToForbidden(true);
          return false;
        }

        if (cancelled) return false;
        dispatch(fromAccounts.doSetSelectedAccount(accountUser?.account));
        return true;
      }

      setRedirectToNotFound(true);
      return false;
    };

    const verifyNewAccountFlow = async (accountId: number) => {
      try {
        if (changeAccount && !cancelled) {
          await changeAccount(accountId);
        }

        const result = await dispatch(fromSubscription.doFetchSubscriptions({ accountId }))
          .then(unwrapResult);

        const hasPaidSubscriptions = result.subscriptionResponse.some(
          sub => sub.source === SubscriptionSource.RECURLY
            && new Date(sub.expiryDate).getTime() > Date.now(),
        );

        const currentPath = location.pathname;

        if (cancelled) return;

        if (flags?.truePlatformEnabled && !hasPaidSubscriptions) {
          // New account - only allow plan, order-confirmation and payment routes
          if (!NEW_ACCOUNT_ALLOWED_PATHS.includes(currentPath)) {
            navigate(plan.path, replaceQuery(queryDictionary()));
            return;
          }
        }
      } catch {
        // If subscription fetch fails, allow access to proceed
      }

      if (cancelled) return;
      setIsAccountAuthenticated(true);
    };

    const fetchInfo = async () => {
      // if accountId is not provided, redirect to account selection page
      if (accountId === null) {
        navigate(accountSelection.path, replaceQuery({
          ...queryDictionary(),
          [ACCOUNT_SELECTION_ROUTE_QUERY_PARAM]: location.pathname,
        }));
        return;
      }

      if (user) {
        setIsAccountAuthenticated(false);
        setAccountAuthenticationError(false);
        setRedirectToForbidden(false);
        setRedirectToNotFound(false);
        setRedirectToUpgradeBlockedForbidden(false);
        if (Number.isNaN(Number(accountId))) {
          // redirect to notfound page in case of an invalid account id
          setRedirectToNotFound(true);
          return;
        }

        // verify if the accountId is opted in starter package
        // redirect to g2 subscription management page
        await verifyStarterPackage(Number(accountId));

        // fetch account user to verify role and permission
        const isAuthorized = await verifyUserAccountRoleAndPermission(user?.id, Number(accountId));

        // verify new account flow - check subscriptions and restrict routes
        if (isAuthorized) {
          await verifyNewAccountFlow(Number(accountId));
        }
      } else {
        setRedirectToNotFound(true);
      }
    };
    fetchInfo().catch(() => {
      if (!cancelled) {
        setIsAccountAuthenticated(false);
        setAccountAuthenticationError(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    user,
    accountId,
    accessibleRoles,
    permissionMapping,
    resource,
    actions,
    isRootOrAdmin,
    isRootOrAdminAccessible,
    isFticUpgradeEntry,
    verificationAttempt,
  ]);

  return {
    isAccountAuthenticated,
    redirectToForbidden,
    redirectToNotFound,
    redirectToUpgradeBlockedForbidden,
    accountAuthenticationError,
    retryAccountAuthentication: () => setVerificationAttempt(attempt => attempt + 1),
  };
};
